/**
 * "Canales de referencia": los canales y videos de referentes con los que se
 * alimenta a Atlas para que piense como ellos.
 *
 * Este archivo es LÓGICA PURA — no toca React, ni la red, ni el storage. Todo
 * lo que decide acá se puede testear sin DOM (`canalesReferencia.test.ts`), que
 * es la única forma de que la validación de una URL pegada a mano no se
 * degrade sin que nadie lo note. La persistencia y el estado viven en
 * `useCanalesReferencia.ts`.
 *
 * Ver ADR-2026-08-17-estudio-marketing-datos-canales-referencia.
 */

import { idEstable } from "./idEstable";

export type PlataformaReferencia = "youtube" | "instagram" | "tiktok";
export type TipoReferencia = "canal" | "video";

export interface FuenteReferencia {
  /**
   * Derivado de la URL canónica: DETERMINISTA, sin `Math.random` ni `Date.now`.
   * Dos sesiones distintas que agregan el mismo canal producen el mismo id, así
   * que lo guardado en el navegador se puede comparar y deduplicar sin arrastrar
   * ids basura.
   */
  id: string;
  nombre: string;
  plataforma: PlataformaReferencia;
  tipo: TipoReferencia;
  /** URL canónica (la que devuelve `interpretarUrlFuente`), no la pegada. */
  url: string;
  /** `true` = viene por defecto (oficial de Meta). No se puede quitar. */
  oficial: boolean;
}

export type MotivoRechazo =
  | "vacia"
  | "no-es-url"
  /** Más larga que cualquier dirección real (ver `MAX_URL_PEGADA`). */
  | "muy-larga"
  | "plataforma-desconocida"
  | "sin-canal-ni-video"
  | "duplicada";

/**
 * El texto que ve el usuario para cada motivo.
 *
 * Vive acá, PEGADO al union que lo indexa, y no en la pantalla, por dos
 * razones: lo consumen dos componentes distintos (el formulario de
 * `CanalesReferencia` y el modal de "Añadir fuente", que agrega por el mismo
 * camino y puede recibir los mismos rechazos) y, siendo un `Record` sobre el
 * union, agregar un motivo sin escribirle el mensaje NO COMPILA. Un motivo
 * nuevo sin texto se vería en pantalla como un error mudo.
 */
export const MENSAJE_RECHAZO: Record<MotivoRechazo, string> = {
  vacia: "Pegá la URL de un canal o de un video.",
  "no-es-url": "Eso no parece una dirección web. Pegá el enlace completo.",
  "muy-larga": "Ese texto es demasiado largo para ser una dirección. Pegá sólo el enlace.",
  "plataforma-desconocida": "Por ahora sólo traemos de YouTube, Instagram y TikTok.",
  "sin-canal-ni-video":
    "Reconocimos la plataforma, pero no el canal ni el video. Pegá el enlace de un canal o de un video.",
  duplicada: "Esa fuente ya está en la lista.",
};

/** Motivos que puede devolver la lectura de una URL (la duplicación se decide
 *  contra la lista, no contra la URL sola). */
export type MotivoLectura = Exclude<MotivoRechazo, "duplicada">;

export type Interpretacion =
  | {
      ok: true;
      plataforma: PlataformaReferencia;
      tipo: TipoReferencia;
      /** URL canónica: misma pieza de contenido ⇒ misma cadena. */
      url: string;
      /** Nombre propuesto si el usuario no escribe uno. */
      nombre: string;
    }
  | { ok: false; motivo: MotivoLectura };

/**
 * Host (ya sin `www.`/`m.`) → plataforma. Es una lista blanca a propósito:
 * cualquier otro dominio se rechaza con `plataforma-desconocida` en vez de
 * aceptarse como "algo de internet" — el bloque promete tres plataformas y
 * tragarse una cuarta sería prometer un procesamiento que no existe.
 */
const PLATAFORMA_POR_HOST: Record<string, PlataformaReferencia> = {
  "youtube.com": "youtube",
  "youtu.be": "youtube",
  "music.youtube.com": "youtube",
  "gaming.youtube.com": "youtube",
  "instagram.com": "instagram",
  "instagr.am": "instagram",
  "tiktok.com": "tiktok",
  "vm.tiktok.com": "tiktok",
  "vt.tiktok.com": "tiktok",
};

const SIN_TIPO: Interpretacion = { ok: false, motivo: "sin-canal-ni-video" };

/** Primeros segmentos de YouTube que identifican un video. */
const YT_VIDEO = ["shorts", "live", "embed", "v"];
/** Primeros segmentos de Instagram que identifican un posteo/reel. */
const IG_VIDEO = ["p", "reel", "reels", "tv"];
/**
 * Rutas de Instagram que NO son un perfil. Sin esta lista,
 * `instagram.com/explore` se leería como "el canal de @explore" y quedaría
 * cargado como referente un usuario que no existe.
 */
const IG_RESERVADOS = new Set([
  "explore",
  "stories",
  "accounts",
  "direct",
  "about",
  "legal",
  "developer",
  "press",
  "api",
  "challenge",
  "emails",
  "session",
  "web",
  "your_activity",
]);

/**
 * El techo de lo pegado. NO es un capricho: lo aceptado se serializa a
 * `localStorage` (URL + nombre + id, tres veces el largo por entrada) y la
 * cuota es de ~5 MB para TODA la app. Sin techo, `instagram.com/` + 5.000
 * caracteres entraba como canal —el MISMO texto tipeado sin el dominio adelante
 * se rechazaba por largo— y llenar la cuota desde este campo hacía fallar en
 * silencio las escrituras de todo lo demás. 2.048 es el límite práctico de una
 * URL en los navegadores: ninguna dirección real queda afuera.
 */
export const MAX_URL_PEGADA = 2048;

/**
 * El nombre PROPUESTO se muestra en la tarjeta, en el aviso verde y en el
 * `aria-label`, y sale de un segmento de la URL: sin recorte, un handle de
 * 2.000 caracteres se dibujaba entero en el aviso (que no trunca). Recorta sólo
 * lo derivado de la URL; el nombre que escribe el usuario no pasa por acá.
 */
const MAX_NOMBRE = 60;

const recortarNombre = (nombre: string) =>
  nombre.length <= MAX_NOMBRE ? nombre : `${nombre.slice(0, MAX_NOMBRE).trimEnd()}…`;

function decodificar(segmento: string): string {
  try {
    return decodeURIComponent(segmento);
  } catch {
    return segmento;
  }
}

const canal = (
  plataforma: PlataformaReferencia,
  url: string,
  nombre: string,
): Interpretacion => ({ ok: true, plataforma, tipo: "canal", url, nombre: recortarNombre(nombre) });

const video = (
  plataforma: PlataformaReferencia,
  url: string,
  nombre: string,
): Interpretacion => ({ ok: true, plataforma, tipo: "video", url, nombre: recortarNombre(nombre) });

/**
 * Lee la URL que el usuario pegó y decide plataforma y tipo.
 *
 * Acepta que venga sin esquema (`youtube.com/@x`, que es lo que sale de la
 * barra del navegador al copiar a mano), pero NO le antepone `https://` a algo
 * que ya trae otro esquema: pegarle un prefijo a `javascript:…` produce una
 * cadena que a veces parsea y siempre miente sobre lo que se pegó.
 */
export function interpretarUrlFuente(crudo: string): Interpretacion {
  const texto = crudo.trim();
  if (!texto) return { ok: false, motivo: "vacia" };
  if (texto.length > MAX_URL_PEGADA) return { ok: false, motivo: "muy-larga" };

  const esHttp = /^https?:\/\//i.test(texto);
  const traeEsquema = /^[a-z][a-z0-9+.-]*:/i.test(texto);
  if (traeEsquema && !esHttp) return { ok: false, motivo: "no-es-url" };

  let url: URL;
  try {
    url = new URL(esHttp ? texto : `https://${texto}`);
  } catch {
    return { ok: false, motivo: "no-es-url" };
  }

  // `www.` y `m.` son la MISMA plataforma; se repiten por si alguien pega
  // `www.m.youtube.com`.
  const host = url.hostname.toLowerCase().replace(/^(?:www\.|m\.)+/, "");
  const plataforma = PLATAFORMA_POR_HOST[host];
  if (!plataforma) return { ok: false, motivo: "plataforma-desconocida" };

  const segmentos = url.pathname.split("/").filter(Boolean).map(decodificar);
  if (plataforma === "youtube") return leerYoutube(host, segmentos, url);
  if (plataforma === "instagram") return leerInstagram(segmentos);
  return leerTiktok(host, segmentos);
}

function leerYoutube(host: string, segmentos: string[], url: URL): Interpretacion {
  const videoYt = (id: string) =>
    video("youtube", `https://www.youtube.com/watch?v=${id}`, "Video de YouTube");

  // Enlace corto: todo `youtu.be/<id>` es un video. Se canoniza a
  // `watch?v=<id>` para que el MISMO video pegado de las dos formas dedupe.
  if (host === "youtu.be") return segmentos[0] ? videoYt(segmentos[0]) : SIN_TIPO;

  const [primero, segundo] = segmentos;
  if (!primero) return SIN_TIPO;
  if (primero === "watch") {
    const v = url.searchParams.get("v");
    return v ? videoYt(v) : SIN_TIPO;
  }
  if (YT_VIDEO.includes(primero)) return segundo ? videoYt(segundo) : SIN_TIPO;
  if (primero.startsWith("@") && primero.length > 1) {
    return canal("youtube", `https://www.youtube.com/${primero}`, primero);
  }
  if (primero === "channel" && segundo) {
    // El id `UC…` no es legible: el nombre lo pone el usuario o queda genérico.
    return canal("youtube", `https://www.youtube.com/channel/${segundo}`, "Canal de YouTube");
  }
  if ((primero === "c" || primero === "user") && segundo) {
    return canal("youtube", `https://www.youtube.com/${primero}/${segundo}`, segundo);
  }
  return SIN_TIPO;
}

function leerInstagram(segmentos: string[]): Interpretacion {
  const [primero, segundo, tercero] = segmentos;
  if (!primero) return SIN_TIPO;

  // `/p/…`, `/reel/…`, `/tv/…`: un posteo. `reels` se canoniza a `reel`, que es
  // la forma que sirve Instagram hoy.
  if (IG_VIDEO.includes(primero)) {
    if (!segundo) return SIN_TIPO;
    const tipo = primero === "reels" ? "reel" : primero;
    return video("instagram", `https://www.instagram.com/${tipo}/${segundo}`, "Video de Instagram");
  }
  /**
   * LA ARROBA NO VA EN LA URL DE INSTAGRAM (sí en YouTube y TikTok, que es de
   * donde viene la confusión). Pegando `instagram.com/@garyvee` —lo que sale de
   * escribir el handle con arroba y agregarle el dominio a mano, o de copiarlo
   * de un texto— el segmento se tomaba CRUDO y salía una canónica que Instagram
   * no resuelve (`/@garyvee`), un nombre `@@garyvee` y, peor, una entrada que
   * NO dedupeaba contra el mismo perfil pegado sin arroba.
   */
  const usuario = primero.replace(/^@+/, "");
  if (!usuario) return SIN_TIPO;
  if (IG_RESERVADOS.has(usuario)) return SIN_TIPO;
  // `/<usuario>/reel/<codigo>`: el mismo posteo con el perfil adelante.
  if (segundo && IG_VIDEO.includes(segundo) && tercero) {
    const tipo = segundo === "reels" ? "reel" : segundo;
    return video(
      "instagram",
      `https://www.instagram.com/${tipo}/${tercero}`,
      `Video de @${usuario}`,
    );
  }
  // Cualquier otra cosa que arranque con un usuario es su perfil — incluidas
  // las solapas (`/<usuario>/reels`, `/<usuario>/tagged`).
  return canal("instagram", `https://www.instagram.com/${usuario}`, `@${usuario}`);
}

function leerTiktok(host: string, segmentos: string[]): Interpretacion {
  const [primero, segundo, tercero] = segmentos;
  // `vm.`/`vt.` son enlaces cortos y SIEMPRE apuntan a un video. No se puede
  // canonizar sin resolver el redirect (que acá no se hace): se guarda tal cual.
  if (host !== "tiktok.com") {
    return primero ? video("tiktok", `https://${host}/${primero}`, "Video de TikTok") : SIN_TIPO;
  }
  if (!primero) return SIN_TIPO;
  if (primero.startsWith("@") && primero.length > 1) {
    if (segundo === "video" && tercero) {
      return video(
        "tiktok",
        `https://www.tiktok.com/${primero}/video/${tercero}`,
        `Video de ${primero}`,
      );
    }
    return canal("tiktok", `https://www.tiktok.com/${primero}`, primero);
  }
  if ((primero === "t" || primero === "video") && segundo) {
    return video("tiktok", `https://www.tiktok.com/${primero}/${segundo}`, "Video de TikTok");
  }
  return SIN_TIPO;
}

/** Dos fuentes son la misma si su URL canónica coincide sin distinguir mayúsculas. */
export const claveFuente = (url: string) => url.toLowerCase();

/**
 * El id de una fuente sale de su clave de dedup (la URL canónica en
 * minúsculas), NO de un slug: aplastar la puntuación a guiones hacía que
 * `instagram.com/gary.vee` y `instagram.com/gary_vee` —dos usuarios válidos y
 * distintos— compartieran id. El dedup es por URL, así que entraban las dos, y
 * `quitarFuente` filtra por id: la ✕ de una borraba LAS DOS, sin aviso y
 * escrito ya en el navegador. Ver `idEstable`.
 *
 * Se deriva de `claveFuente(url)` a propósito: dos formas de escribir la misma
 * URL que el dedup considera UNA tienen que dar el MISMO id.
 */
const idDesdeUrl = (url: string) => idEstable("ref", claveFuente(url));

/**
 * Las fuentes oficiales de Meta, cargadas POR DEFECTO. No se pueden quitar: son
 * la línea de base de la que sale todo lo que Atlas sabe de pauta y contenido en
 * las plataformas de Meta.
 *
 * Declaran `plataforma` y `tipo` a mano —son datos, no resultado de parsear—
 * pero el test exige que coincidan con lo que `interpretarUrlFuente` lee de su
 * URL: si alguien pega mal una, el test lo canta.
 */
export const FUENTES_META: FuenteReferencia[] = [
  {
    id: "meta-yt-business",
    nombre: "Meta for Business",
    plataforma: "youtube",
    tipo: "canal",
    url: "https://www.youtube.com/@MetaforBusiness",
    oficial: true,
  },
  {
    id: "meta-yt-meta",
    nombre: "Meta",
    plataforma: "youtube",
    tipo: "canal",
    url: "https://www.youtube.com/@Meta",
    oficial: true,
  },
  {
    id: "meta-ig-meta",
    nombre: "Meta",
    plataforma: "instagram",
    tipo: "canal",
    url: "https://www.instagram.com/meta",
    oficial: true,
  },
];

/** Referentes de marketing que se pueden sumar de un toque. */
export interface SugerenciaReferente {
  nombre: string;
  url: string;
}

export const SUGERENCIAS: SugerenciaReferente[] = [
  { nombre: "Gary Vaynerchuk", url: "https://www.youtube.com/@garyvee" },
  { nombre: "Neil Patel", url: "https://www.youtube.com/@neilpatel" },
  { nombre: "HubSpot Marketing", url: "https://www.youtube.com/@HubSpotMarketing" },
  { nombre: "Gary Vaynerchuk", url: "https://www.instagram.com/garyvee" },
  { nombre: "Gary Vaynerchuk", url: "https://www.tiktok.com/@garyvee" },
];

/**
 * La lista que se muestra: las oficiales de Meta primero y después las
 * agregadas, sin repetidos. La PRIMERA ocurrencia gana, así que una agregada
 * que apunte a una URL de Meta no la duplica ni la desplaza.
 *
 * `oficial` se fuerza a `false` en todo lo agregado: el campo decide si aparece
 * el botón de quitar, y un dato guardado en el navegador (editable desde el
 * DevTools) no puede convertirse en "oficial de Meta" y volverse indeleble.
 */
export function armarListaFuentes(agregadas: FuenteReferencia[]): FuenteReferencia[] {
  const vistas = new Set<string>();
  const lista: FuenteReferencia[] = [];
  for (const fuente of FUENTES_META) {
    const clave = claveFuente(fuente.url);
    if (vistas.has(clave)) continue;
    vistas.add(clave);
    lista.push(fuente);
  }
  for (const fuente of agregadas) {
    const clave = claveFuente(fuente.url);
    if (vistas.has(clave)) continue;
    vistas.add(clave);
    lista.push({ ...fuente, oficial: false });
  }
  return lista;
}

export type ResultadoAgregar =
  | { ok: true; agregadas: FuenteReferencia[]; fuente: FuenteReferencia }
  | { ok: false; motivo: MotivoRechazo };

/**
 * Agrega una fuente a partir de la URL pegada. Devuelve la lista NUEVA (no
 * muta) o el motivo del rechazo, para que la pantalla pueda decir exactamente
 * qué pasó — "no es de ninguna de las tres plataformas" y "esa ya está" son
 * problemas distintos y un solo "URL inválida" para los dos no ayuda a nadie.
 */
export function agregarFuenteDesdeUrl(
  agregadas: FuenteReferencia[],
  crudo: string,
  nombre?: string,
): ResultadoAgregar {
  const leida = interpretarUrlFuente(crudo);
  if (!leida.ok) return { ok: false, motivo: leida.motivo };
  const clave = claveFuente(leida.url);
  if (armarListaFuentes(agregadas).some((f) => claveFuente(f.url) === clave)) {
    return { ok: false, motivo: "duplicada" };
  }
  const fuente: FuenteReferencia = {
    id: idDesdeUrl(leida.url),
    nombre: (nombre ?? "").trim() || leida.nombre,
    plataforma: leida.plataforma,
    tipo: leida.tipo,
    url: leida.url,
    oficial: false,
  };
  return { ok: true, agregadas: [...agregadas, fuente], fuente };
}

/**
 * Une lo que ya estaba GUARDADO para una marca con lo que se agregó antes de
 * que esa marca terminara de resolverse, sin repetidos (por `claveFuente`) y
 * conservando el orden: primero lo guardado, después lo nuevo.
 *
 * EXISTE PARA QUE NO SE BORRE NADA. `useCanalesReferencia` escribía la clave de
 * la marca directamente con la lista pendiente —copiando el patrón de
 * `useConfigMapa`, donde lo pendiente es el objeto de config COMPLETO y pisarlo
 * es correcto—, pero acá lo pendiente es un DELTA calculado sobre una lista
 * vacía (mientras no hay marca no se puede leer nada del storage). Escribirlo
 * tal cual no era "la última elección del usuario le gana a lo guardado": era
 * truncar la lista a lo agregado en los últimos dos segundos y perder en
 * silencio todo lo anterior (ADR-2026-08-17-estudio-marketing-fixes-revision).
 */
export function fusionarFuentes(
  guardadas: FuenteReferencia[],
  pendientes: FuenteReferencia[],
): FuenteReferencia[] {
  const vistas = new Set<string>();
  const lista: FuenteReferencia[] = [];
  for (const fuente of [...guardadas, ...pendientes]) {
    const clave = claveFuente(fuente.url);
    if (vistas.has(clave)) continue;
    vistas.add(clave);
    lista.push(fuente);
  }
  return lista;
}

/** Saca una agregada por id. Las oficiales de Meta no están en esta lista, así
 *  que no hay forma de borrarlas ni pasando su id. */
export const quitarFuente = (agregadas: FuenteReferencia[], id: string): FuenteReferencia[] =>
  agregadas.filter((f) => f.id !== id);

/**
 * Una sugerencia lista para DIBUJARSE con la misma tarjeta que una fuente
 * cargada: además del nombre y la URL, la plataforma y el tipo que se leen de
 * esa URL.
 *
 * Van acá y no en la pantalla porque las dos tarjetas —la sugerida y la
 * cargada— muestran exactamente los mismos datos (logo de la plataforma,
 * nombre, "Canal de YouTube"), y la única forma de que se vean iguales de
 * verdad es que los reciban del mismo lugar. Derivarlos en el JSX obligaba a
 * volver a llamar a `interpretarUrlFuente` y a estrechar un union que esta
 * función ya estrechó para filtrar.
 */
export interface SugerenciaDisponible extends SugerenciaReferente {
  plataforma: PlataformaReferencia;
  tipo: TipoReferencia;
}

/** Las sugerencias que todavía NO están en la lista: agregar algo que ya está
 *  no haría nada y la tarjeta quedaría mintiendo. */
export function sugerenciasDisponibles(agregadas: FuenteReferencia[]): SugerenciaDisponible[] {
  const cargadas = new Set(armarListaFuentes(agregadas).map((f) => claveFuente(f.url)));
  const lista: SugerenciaDisponible[] = [];
  for (const sugerencia of SUGERENCIAS) {
    const leida = interpretarUrlFuente(sugerencia.url);
    if (!leida.ok || cargadas.has(claveFuente(leida.url))) continue;
    // `nombre` y `url` quedan los de `SUGERENCIAS` (la URL de la lista ya es
    // canónica): lo que se agrega son los dos campos derivados.
    lista.push({ ...sugerencia, plataforma: leida.plataforma, tipo: leida.tipo });
  }
  return lista;
}

/**
 * Interpreta lo guardado en el navegador. PURA (recibe el string crudo) y
 * BLINDADA, con el mismo criterio que `leerConfigMapa`: cualquier cosa que no
 * sea la lista esperada se descarta en vez de tumbar la pantalla.
 *
 * La `plataforma` y el `tipo` NO se leen del JSON: se vuelven a derivar de la
 * URL. Lo guardado es editable desde el DevTools y esos dos campos deciden qué
 * logo se dibuja — leerlos del storage permitiría mostrar un video de TikTok
 * con el logo de YouTube.
 */
export function leerCanalesGuardados(crudo: string | null): FuenteReferencia[] {
  if (!crudo) return [];
  let dato: unknown;
  try {
    dato = JSON.parse(crudo);
  } catch {
    return [];
  }
  if (!Array.isArray(dato)) return [];
  const vistas = new Set<string>();
  const lista: FuenteReferencia[] = [];
  for (const item of dato) {
    if (typeof item !== "object" || item === null) continue;
    const guardado = item as Record<string, unknown>;
    if (typeof guardado.url !== "string") continue;
    const leida = interpretarUrlFuente(guardado.url);
    if (!leida.ok) continue;
    const clave = claveFuente(leida.url);
    if (vistas.has(clave)) continue;
    vistas.add(clave);
    const nombre = typeof guardado.nombre === "string" ? guardado.nombre.trim() : "";
    lista.push({
      id: idDesdeUrl(leida.url),
      nombre: nombre || leida.nombre,
      plataforma: leida.plataforma,
      tipo: leida.tipo,
      url: leida.url,
      oficial: false,
    });
  }
  return lista;
}

/* ══════════════════════════════════════════════════════════════════════════
   AÑADIR FUENTE — las dos lecturas que pide el modal
   ══════════════════════════════════════════════════════════════════════════

   El modal de "Añadir fuente" no pide "una URL" a secas como el formulario de
   abajo: pide UNA COSA CONCRETA por atajo, y cada campo tiene que rechazar lo
   que no es esa cosa. Pegar el link de un canal donde se pide un video no es
   un error de tipeo — es material distinto, que Atlas va a leer distinto
   cuando exista el procesamiento— y aceptarlo en silencio dejaría al usuario
   creyendo que cargó un video.

   Las dos funciones se apoyan en `interpretarUrlFuente` (mismo parser, mismas
   canónicas, mismo dedup): lo que agregan es el ESTRECHAMIENTO —"tiene que ser
   un video", "tiene que ser un canal de ESTA plataforma"— y, para Instagram y
   TikTok, un camino que no pasa por ninguna URL: el usuario suelto.
   Ver ADR-2026-08-17-estudio-marketing-modal-anadir-fuente. */

/** "Es un canal" no es "no lo entendí": la única salida es cambiar de atajo. */
export type MotivoLinkVideo = MotivoLectura | "es-un-canal";

export type LecturaLinkVideo =
  | { ok: true; plataforma: PlataformaReferencia; url: string; nombre: string }
  | { ok: false; motivo: MotivoLinkVideo };

export const MENSAJE_LINK_VIDEO: Record<MotivoLinkVideo, string> = {
  vacia: "Pegá el link del video.",
  "no-es-url": "Eso no parece un link. Pegá la dirección completa del video.",
  "muy-larga": "Ese texto es demasiado largo para ser un link. Pegá sólo la dirección del video.",
  "plataforma-desconocida": "Por ahora sólo leemos videos de YouTube, Instagram y TikTok.",
  "sin-canal-ni-video": "Reconocimos la plataforma, pero no el video. Pegá el link del video en sí.",
  "es-un-canal": "Ese link es de un canal, no de un video. Usá “Vincular canal de creador”.",
};

/** Un link de video de las tres plataformas. Rechaza el de un CANAL con su
 *  propio motivo, que es el que permite mandarlo al atajo correcto. */
export function interpretarLinkVideo(crudo: string): LecturaLinkVideo {
  const leida = interpretarUrlFuente(crudo);
  if (!leida.ok) return { ok: false, motivo: leida.motivo };
  if (leida.tipo === "canal") return { ok: false, motivo: "es-un-canal" };
  return { ok: true, plataforma: leida.plataforma, url: leida.url, nombre: leida.nombre };
}

export type MotivoCanalCreador =
  | "vacio"
  /** Se pidió un LINK (YouTube) y llegó otra cosa, o llegó algo que no parsea. */
  | "no-es-link"
  /** Se pidió un USUARIO (Instagram/TikTok) y no cumple las reglas de esa plataforma. */
  | "usuario-invalido"
  /** Ni un usuario ni un link: un pegado que se pasa de `MAX_URL_PEGADA`. */
  | "muy-largo"
  | "otra-plataforma"
  | "es-un-video"
  | "sin-canal";

export type LecturaCanalCreador =
  | { ok: true; plataforma: PlataformaReferencia; url: string; nombre: string }
  | { ok: false; motivo: MotivoCanalCreador };

/**
 * Las reglas de usuario de cada plataforma. NO son las mismas y por eso están
 * declaradas por separado: en Instagram `a` es un usuario posible y en TikTok
 * no (mínimo 2); un usuario de 30 caracteres es válido en Instagram y pasado
 * de largo en TikTok (máximo 24); un punto adelante lo rechaza Instagram y lo
 * acepta TikTok. Un validador único con la unión de las reglas aceptaría
 * usuarios que la plataforma no permite crear, y el link quedaría muerto.
 */
const REGLAS_USUARIO: Record<
  "instagram" | "tiktok",
  { min: number; max: number; puntoAlPrincipio: boolean }
> = {
  instagram: { min: 1, max: 30, puntoAlPrincipio: false },
  tiktok: { min: 2, max: 24, puntoAlPrincipio: true },
};

const USUARIO_VALIDO = /^[a-z0-9._]+$/i;

/** El usuario tal cual lo pegan: con arroba, sin arroba, con espacios al lado
 *  o con la barra final que arrastra el copiar de la barra del navegador. */
const limpiarUsuario = (crudo: string) => crudo.trim().replace(/^@+/, "").replace(/\/+$/, "");

function usuarioValido(plataforma: "instagram" | "tiktok", usuario: string): boolean {
  const reglas = REGLAS_USUARIO[plataforma];
  if (usuario.length < reglas.min || usuario.length > reglas.max) return false;
  if (!USUARIO_VALIDO.test(usuario)) return false;
  // Dos puntos seguidos y punto al final no existen en ninguna de las dos.
  if (usuario.includes("..") || usuario.endsWith(".")) return false;
  if (!reglas.puntoAlPrincipio && usuario.startsWith(".")) return false;
  // `instagram.com/explore` no es "el perfil de @explore" ni acá ni pegando la
  // URL: es la misma lista de rutas del sitio que usa el parser.
  if (plataforma === "instagram" && IG_RESERVADOS.has(usuario.toLowerCase())) return false;
  return true;
}

/**
 * ¿Esto es un intento de URL o un usuario suelto?
 *
 * No alcanza con "tiene un punto": `gary.vee` es un usuario de Instagram
 * perfectamente válido, y tratarlo como dominio lo mandaría a
 * "otra-plataforma" — un error que el usuario no puede corregir porque no hizo
 * nada mal. Se considera URL sólo si trae esquema, si trae una barra, o si es
 * exactamente uno de los hosts que el parser conoce.
 */
const pareceUrl = (texto: string): boolean => {
  if (/^[a-z][a-z0-9+.-]*:/i.test(texto)) return true;
  if (texto.includes("/")) return true;
  const host = texto.toLowerCase().replace(/^(?:www\.|m\.)+/, "");
  return host in PLATAFORMA_POR_HOST;
};

const MOTIVO_DESDE_LECTURA: Record<MotivoLectura, MotivoCanalCreador> = {
  vacia: "vacio",
  "no-es-url": "no-es-link",
  "muy-larga": "muy-largo",
  "plataforma-desconocida": "otra-plataforma",
  "sin-canal-ni-video": "sin-canal",
};

/**
 * El usuario que quedó en la URL canónica de un perfil
 * (`https://www.instagram.com/<usuario>`, `https://www.tiktok.com/@<usuario>`).
 * Se lee de la CANÓNICA y no del texto pegado: ahí ya no hay solapas, ni barra
 * final, ni query.
 */
function usuarioDeCanonica(url: string): string {
  try {
    const segmento = new URL(url).pathname.split("/").filter(Boolean)[0] ?? "";
    return limpiarUsuario(decodificar(segmento));
  } catch {
    return "";
  }
}

/** La URL y el nombre de un perfil, armados en UN solo lugar: el usuario
 *  tipeado y el pegado como URL tienen que terminar en la misma fuente (misma
 *  canónica ⇒ mismo dedup, mismo id). */
const perfilDeUsuario = (
  plataforma: "instagram" | "tiktok",
  usuario: string,
): { url: string; nombre: string } => ({
  url:
    plataforma === "instagram"
      ? `https://www.instagram.com/${usuario}`
      : `https://www.tiktok.com/@${usuario}`,
  nombre: `@${usuario}`,
});

/**
 * El canal de un creador en UNA plataforma elegida de antemano.
 *
 * YouTube pide el LINK del canal (no hay usuario suelto que alcance: conviven
 * `@handle`, `/channel/UC…`, `/c/…` y `/user/…`, y adivinar cuál es armaría
 * links rotos). Instagram y TikTok piden el USUARIO, y aceptan además que
 * peguen la URL del perfil, que es lo que sale de compartir desde la app.
 */
export function interpretarCanalCreador(
  plataforma: PlataformaReferencia,
  crudo: string,
): LecturaCanalCreador {
  const texto = crudo.trim();
  if (!texto) return { ok: false, motivo: "vacio" };

  if (pareceUrl(texto)) {
    const leida = interpretarUrlFuente(texto);
    if (!leida.ok) return { ok: false, motivo: MOTIVO_DESDE_LECTURA[leida.motivo] };
    if (leida.plataforma !== plataforma) return { ok: false, motivo: "otra-plataforma" };
    if (leida.tipo === "video") return { ok: false, motivo: "es-un-video" };
    // LAS MISMAS REGLAS QUE EL USUARIO TIPEADO. El parser genérico no las
    // conoce (acepta cualquier primer segmento que no sea una ruta del sitio),
    // así que por acá se colaba lo que el campo rechaza escrito a mano:
    // `gary..vee`, `garyvee.`, un usuario de 200 caracteres o uno de una sola
    // letra en TikTok. Dos veredictos distintos para el mismo dato según cómo
    // se escriba, y un referente cargado con un link que la plataforma no
    // sirve. YouTube queda afuera porque no tiene reglas de usuario: conviven
    // `@handle`, `/channel/UC…`, `/c/…` y `/user/…`.
    if (plataforma !== "youtube") {
      const usuarioUrl = usuarioDeCanonica(leida.url);
      if (!usuarioValido(plataforma, usuarioUrl)) {
        return { ok: false, motivo: "usuario-invalido" };
      }
      return { ok: true, plataforma, ...perfilDeUsuario(plataforma, usuarioUrl) };
    }
    return { ok: true, plataforma, url: leida.url, nombre: leida.nombre };
  }

  // No es una URL: sólo Instagram y TikTok tienen un camino por acá.
  if (plataforma === "youtube") return { ok: false, motivo: "no-es-link" };

  const usuario = limpiarUsuario(texto);
  if (!usuarioValido(plataforma, usuario)) return { ok: false, motivo: "usuario-invalido" };
  return { ok: true, plataforma, ...perfilDeUsuario(plataforma, usuario) };
}

/**
 * El mensaje del rechazo DEPENDE de la plataforma, no sólo del motivo: para
 * YouTube hay que decir "pegá el link" y para Instagram "escribí el usuario",
 * y son la misma situación (`no-es-link` / `usuario-invalido`) contada al revés.
 * Un único texto para los tres mandaría a la mitad de los usuarios a hacer lo
 * que el campo justamente no acepta.
 */
export function mensajeCanalCreador(
  plataforma: PlataformaReferencia,
  motivo: MotivoCanalCreador,
): string {
  const nombre = plataforma === "youtube" ? "YouTube" : plataforma === "instagram" ? "Instagram" : "TikTok";
  switch (motivo) {
    case "vacio":
      return plataforma === "youtube"
        ? "Pegá el link del canal."
        : `Escribí el usuario de ${nombre}.`;
    case "no-es-link":
      return plataforma === "youtube"
        ? "Para YouTube necesitamos el link del canal (youtube.com/@nombre, /channel/… o /c/…), no el usuario suelto."
        : `Eso no parece un usuario ni un link de ${nombre}.`;
    case "usuario-invalido": {
      const reglas = REGLAS_USUARIO[plataforma === "instagram" ? "instagram" : "tiktok"];
      return `Ese usuario no existe en ${nombre}: entre ${reglas.min} y ${reglas.max} caracteres, con letras, números, puntos y guiones bajos.`;
    }
    case "muy-largo":
      return plataforma === "youtube"
        ? "Ese texto es demasiado largo para ser un link. Pegá sólo la dirección del canal."
        : `Ese texto es demasiado largo: no es un usuario ni un link de ${nombre}.`;
    case "otra-plataforma":
      return `Ese link no es de ${nombre}. Elegí la plataforma que corresponde o pegá el link del canal de ${nombre}.`;
    case "es-un-video":
      return "Ese link es de un video, no de un canal. Usá “Extraer conocimiento de video”.";
    case "sin-canal":
      return `Reconocimos ${nombre}, pero no el canal. Pegá el link del perfil.`;
  }
}

/* ────────────────────────────────────────────────────────────────────────────
   La tarjeta del creador, a partir del link de un video.

   Cuando alguien pega el link de un video, debajo aparece una tarjeta con quién
   lo hizo y la opción de sumar su canal entero. El problema es que ese dato
   NO está en la URL para todas las plataformas, y acá no hay backend que salga
   a preguntarle a nadie. Esto devuelve SÓLO lo que se puede derivar del link
   sin pedirle permiso a ninguna API:

     · TikTok    → el usuario está en la ruta (`/@usuario/video/123`), así que
                   sale el nombre Y el canal. Miniatura no: la de TikTok exige
                   una llamada a su oEmbed.
     · YouTube   → la miniatura sí, porque su dirección se arma con el id del
                   video y es pública. El nombre del canal NO está en la URL de
                   un `watch?v=`; sólo aparece si el link ya venía con el handle
                   (`/@handle/...`), que es el caso de los shorts compartidos
                   desde el perfil.
     · Instagram → ni una cosa ni la otra. Un `/reel/ABC` no dice de quién es.

   Lo que falta NO se inventa: la interfaz muestra un hueco rotulado y dice que
   se completa cuando exista la lectura del link. Un nombre de creador inventado
   en una pantalla que existe para cargar referentes sería exactamente el tipo
   de mentira que el resto de este bloque se ocupa de no decir.
   ──────────────────────────────────────────────────────────────────────────── */

export interface DatosCreador {
  plataforma: PlataformaReferencia;
  /** El @usuario, cuando la ruta lo trae. `null` = hay que ir a buscarlo. */
  usuario: string | null;
  /** Miniatura pública, sin credenciales. Hoy sólo YouTube la tiene predecible. */
  miniatura: string | null;
  /** El canal a sumar con “Añadir canal completo”, si se puede armar sin API. */
  urlCanal: string | null;
}

/** El id del video de YouTube, en los tres formatos que se comparten. */
function idDeYoutube(url: URL, host: string): string | null {
  if (host === "youtu.be") return url.pathname.slice(1).split("/")[0] || null;
  const v = url.searchParams.get("v");
  if (v) return v;
  const porRuta = url.pathname.match(/^\/(?:shorts|embed|live|v)\/([^/?#]+)/);
  return porRuta ? porRuta[1] : null;
}

/** El `@handle` de la ruta, si lo hay. Sirve para YouTube y para TikTok. */
function handleDeRuta(url: URL): string | null {
  const m = url.pathname.match(/^\/@([^/?#]+)/);
  return m ? m[1] : null;
}

/**
 * Qué se puede decir del creador de un video mirando SÓLO su link.
 * Devuelve `null` si el texto no es un link de video de una plataforma conocida
 * — el llamador ya validó con `interpretarLinkVideo`, esto sólo enriquece.
 */
export function datosCreadorDeVideo(crudo: string): DatosCreador | null {
  const texto = crudo.trim();
  if (!texto) return null;

  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(texto) ? texto : `https://${texto}`);
  } catch {
    return null;
  }

  const host = url.hostname.toLowerCase().replace(/^(?:www\.|m\.)+/, "");

  if (host === "youtube.com" || host === "youtu.be") {
    const id = idDeYoutube(url, host);
    const handle = handleDeRuta(url);
    return {
      plataforma: "youtube",
      usuario: handle,
      // `i.ytimg.com` es el CDN de miniaturas de YouTube: pública, sin API key.
      // `mqdefault` existe para TODO video; `maxresdefault` no (los viejos y
      // los de baja resolución devuelven 404 y dejarían la tarjeta rota).
      miniatura: id ? `https://i.ytimg.com/vi/${id}/mqdefault.jpg` : null,
      urlCanal: handle ? `https://www.youtube.com/@${handle}` : null,
    };
  }

  if (host === "tiktok.com") {
    const handle = handleDeRuta(url);
    return {
      plataforma: "tiktok",
      usuario: handle,
      miniatura: null,
      urlCanal: handle ? `https://www.tiktok.com/@${handle}` : null,
    };
  }

  if (host === "instagram.com") {
    // Un `/reel/ABC` o `/p/ABC` no dice de quién es. Se devuelve la plataforma
    // igual, para que la tarjeta pueda aparecer con sus huecos rotulados en vez
    // de no aparecer y dejar al usuario sin saber que el link se entendió.
    return { plataforma: "instagram", usuario: null, miniatura: null, urlCanal: null };
  }

  return null;
}
